import { config as appConfig } from '@/config';

const INITIAL_RETRY_DELAY_MS = 15_000;
const MAX_RETRY_DELAY_MS = 120_000;

type AvailabilityListener = () => void;

interface AgentServiceAvailabilityState {
  consecutiveFailures: number;
  unavailableUntil: number;
  lastFailureMessage: string | null;
  knownAvailable: boolean;
}

const listeners = new Set<AvailabilityListener>();

let availabilityState: AgentServiceAvailabilityState = {
  consecutiveFailures: 0,
  unavailableUntil: 0,
  lastFailureMessage: null,
  knownAvailable: false,
};
let availabilityProbe: Promise<boolean> | null = null;

const notifyListeners = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

const getRetryDelayMs = (failureCount: number): number =>
  Math.min(
    INITIAL_RETRY_DELAY_MS * 2 ** Math.max(0, failureCount - 1),
    MAX_RETRY_DELAY_MS,
  );

export const getAgentServiceAvailabilitySnapshot = (
  now = Date.now(),
): {
  isBackoffActive: boolean;
  retryInMs: number;
  consecutiveFailures: number;
  lastFailureMessage: string | null;
  knownAvailable: boolean;
} => {
  const retryInMs = Math.max(0, availabilityState.unavailableUntil - now);

  return {
    isBackoffActive: retryInMs > 0,
    retryInMs,
    consecutiveFailures: availabilityState.consecutiveFailures,
    lastFailureMessage: availabilityState.lastFailureMessage,
    knownAvailable: availabilityState.knownAvailable,
  };
};

export const shouldSkipOptionalAgentServiceRequest = (now = Date.now()): boolean =>
  getAgentServiceAvailabilitySnapshot(now).isBackoffActive;

const getCurrentOrigin = (): string =>
  typeof window === 'undefined' ? 'http://localhost' : window.location.origin;

export const getAgentServiceHealthUrl = (): string => {
  const parsedUrl = new URL(appConfig.api.agentServiceUrl, getCurrentOrigin());
  const basePath = parsedUrl.pathname === '/' ? '' : parsedUrl.pathname.replace(/\/$/, '');
  return `${parsedUrl.origin}${basePath}/health`;
};

export const ensureOptionalAgentServiceAvailable = async (
  options: { probe?: boolean } = {},
): Promise<boolean> => {
  if (shouldSkipOptionalAgentServiceRequest()) {
    return false;
  }

  if (availabilityState.knownAvailable) {
    return true;
  }

  if (options.probe !== true) {
    return false;
  }

  if (availabilityProbe) {
    return availabilityProbe;
  }

  availabilityProbe = (async () => {
    try {
      const response = await fetch(getAgentServiceHealthUrl(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        recordAgentServiceSuccess();
        return true;
      }

      recordAgentServiceFailure(`Agent service health check failed with status ${response.status}`);
      return false;
    } catch (error) {
      recordAgentServiceFailure(error instanceof Error ? error.message : 'Agent service health check failed');
      return false;
    }
  })().finally(() => {
    availabilityProbe = null;
  });

  return availabilityProbe;
};

export const recordAgentServiceSuccess = (): void => {
  if (
    availabilityState.knownAvailable &&
    availabilityState.consecutiveFailures === 0 &&
    availabilityState.unavailableUntil === 0 &&
    availabilityState.lastFailureMessage === null
  ) {
    return;
  }

  availabilityState = {
    consecutiveFailures: 0,
    unavailableUntil: 0,
    lastFailureMessage: null,
    knownAvailable: true,
  };
  notifyListeners();
};

export const recordAgentServiceFailure = (
  message: string | null = null,
  now = Date.now(),
): void => {
  const consecutiveFailures = availabilityState.consecutiveFailures + 1;
  availabilityState = {
    consecutiveFailures,
    unavailableUntil: now + getRetryDelayMs(consecutiveFailures),
    lastFailureMessage: message,
    knownAvailable: false,
  };
  notifyListeners();
};

export const subscribeAgentServiceAvailability = (
  listener: AvailabilityListener,
): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const resetAgentServiceAvailabilityForTests = (): void => {
  availabilityState = {
    consecutiveFailures: 0,
    unavailableUntil: 0,
    lastFailureMessage: null,
    knownAvailable: false,
  };
  availabilityProbe = null;
  notifyListeners();
};
